import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation, Outlet } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleGuard from '../guards/RoleGuard';
import AplusGuard from '../guards/AplusGuard';
import { PATH_DASHBOARD } from './paths';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import LoadingScreen from '../components/LoadingScreen';

import { useRole, useRoleGroup } from 'lib/evi-role-action/hoc';
import customList from 'redux/slices/customList';

// ----------------------------------------------------------------------

const Loadable = (Component: React.ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const [UserRole] = useRole({
    type: 'staff',
    module: 'user',
    controller: 'staff',
    action: ['get', 'get_list', 'add', 'update', 'delete', 'add_teacher', 'add_student']
  });

  const [GroupRole] = useRole({
    type: 'staff',
    module: 'user',
    controller: 'group',
    action: ['get_list', 'get']
  });

  const [DrpcUserRole] = useRole({
    type: 'staff',
    module: 'drpc_user',
    controller: 'main'
  });

  const [BuilderRole] = useRole({
    type: 'staff',
    module: 'user',
    controller: 'builder'
  });

  const [DocumentRole] = useRole({
    type: 'staff',
    module: 'drpc_course',
    controller: 'document'
  });

  const [MerchantM] = useRoleGroup({
    type: 'staff',
    group: 'STAFF_ROLE_SUB_MERCHANT'
  });

  const [BuilderM] = useRoleGroup({
    type: 'staff',
    group: 'STAFF_BUILDER_ADMIN'
  });

  const [ContractM] = useRoleGroup({
    type: 'staff',
    group: 'STAFF_ROLE_CONTRACT_ADMIN'
  });

  return useRoutes([
    {
      path: '/',
      children: [
        { element: <Navigate to="/login" replace /> },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'merchant',
          element: (
            <GuestGuard>
              <Merchant />
            </GuestGuard>
          )
        },
        {
          path: 'loading',
          element: <AutoLogin />
        },
        // {
        //   path: 'register',
        //   element: (
        //     <GuestGuard>
        //       <Register />
        //     </GuestGuard>
        //   )
        // },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
        // 資源獨立展示頁（無 dashboard 側欄/頂部），供 img_real 等類型新窗口打開
        {
          path: 'resource-view/:ref_id',
          element: (
            <AuthGuard>
              <ResourceCheck />
            </AuthGuard>
          )
        },
        // AIQBP-78 smoke-test page — dev only. Mounts GodotPreviewIframe
        // with a synthetic level + fake auth so the WASM boot + bridge
        // handshake + EDIT_* roundtrip can be exercised without the editor.
        ...(process.env.NODE_ENV !== 'production'
          ? [{ path: 'preview-test', element: <GodotPreviewTest /> }]
          : [])
      ]
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/home" replace /> },
        { path: 'home', element: <DashboardHome /> },
        { path: 'app', element: <GeneralApp /> },
        {
          path: 'school-based',
          element: (
            <RoleGuard ctrl="resource_course" role={['get_list']} rolestring="get_list">
              {/* 这里可以是一个 Outlet 或者具体 Layout */}
              <Outlet />
            </RoleGuard>
          ),
          children: [
            { element: <GeneralSchoolBasedActivities /> },
            {
              path: 'add',
              element: (
                <RoleGuard ctrl="resource_course" role={['add']} rolestring="add">
                  <GeneralSchoolBasedActivityForm />
                </RoleGuard>
              )
            },
            // { path: ':activity_id', element: <GeneralSchoolBasedActivityForm /> },
            { path: 'edit/:activity_id', element: <GeneralSchoolBasedActivityEditForm /> },
            {
              path: 'edit/:activity_id/view-report',
              element: <GeneralSchoolBasedActivityViewStudentReport />
            },
            {
              path: 'edit/:activity_id/view-report/:student_id',
              element: <GeneralSchoolBasedActivityViewStudentReportDetail />
            },
            {
              path: 'view-state/:activity_id',
              element: <GeneralSchoolBasedActivityViewState />
            },
            {
              path: 'view-state/:activity_id/class/:class_id',
              element: <GeneralSchoolBasedActivityViewStateStudent />
            },
            {
              path: 'view-state/:activity_id/class/:class_id/student/:student_id',
              element: <GeneralSchoolBasedActivityViewStateStudentDetail />
            }
            // { path: ':course_id', element: <GeneralSchoolCourse /> },
            // { path: ':course_id/:section', element: <GeneralSchoolCourse /> }
          ]
        },
        {
          path: 'curriculum-activities',
          element: (
            <RoleGuard ctrl="resource_course_new" role={['get_list']} rolestring="get_list">
              <Outlet />
            </RoleGuard>
          ),
          children: [
            {
              element: (
                <RoleGuard ctrl="resource_course_new" role={['get_list']} rolestring="get_list">
                  <CurriculumActivities />
                </RoleGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleGuard ctrl="resource_course_new" role={['add']} rolestring="add">
                  <CurriculumActivitiesNew />
                </RoleGuard>
              )
            },
            { path: 'edit/:activity_id', element: <CurriculumActivitiesEdit /> },
            { path: 'view-state/:activity_id', element: <GeneralSchoolBasedActivityViewState /> },
            {
              path: 'view-state/:activity_id/class/:class_id',
              element: <GeneralSchoolBasedActivityViewStateStudent />
            },
            {
              path: 'view-state/:activity_id/class/:class_id/student/:student_id',
              element: <GeneralSchoolBasedActivityViewStateStudentDetail />
            }
          ]
        },
        {
          path: 'SchoolCourse',
          children: [
            { element: <GeneralSchoolCourse /> },
            { path: ':course_id', element: <GeneralSchoolCourse /> },
            { path: ':course_id/:section', element: <GeneralSchoolCourse /> }
          ]
        },
        {
          path: 'SmallActivity',
          children: [
            { element: <GeneralSmallActivity /> },
            { path: ':course_id/:res_id', element: <GeneralSmallActivity /> }
          ]
        },
        {
          path: 'Exhibition',
          element: (
            <RoleGuard ctrl="resource_wonder_wall" role={['get_list']} rolestring="get_list">
              {/* 这里可以是一个 Outlet 或者具体 Layout */}
              <GeneralExhibition />
            </RoleGuard>
          )
        },
        {
          path: 'Exhibition/detail/:id',
          element: (
            <RoleGuard ctrl="resource_wonder_wall" role={['get']} rolestring="get">
              <GeneralExhibitionDetail />
            </RoleGuard>
          )
        },
        {
          path: 'ecommerce',
          element: (
            <RoleGuard ctrl="resource_resource_hub" role={['get_list']} rolestring="get_list">
              <GeneralPlatform />
            </RoleGuard>
          )
        },
        { path: 'analytics', element: <GeneralAnalytics /> },
        {
          path: 'customList',
          children: [
            { element: <GeneralCustomList /> },
            { path: ':display_id', element: <GeneralCustomListDetail /> }
          ]
        },
        {
          path: 'parentChildTask',
          element: (
            <RoleGuard ctrl="resource_parent_child" role={['get_list']} rolestring="get_list">
              {/* 这里可以是一个 Outlet 或者具体 Layout */}
              <Outlet />
            </RoleGuard>
          ),
          children: [
            {
              path: 'list',
              element: (
                <RoleGuard ctrl="resource_parent_child" role={['get_list']} rolestring="get_list">
                  {/* 这里可以是一个 Outlet 或者具体 Layout */}
                  <ParentChildTaskList />
                </RoleGuard>
              )
            },
            {
              path: 'add',
              element: (
                <RoleGuard ctrl="resource_parent_child" role={['add']} rolestring="add">
                  {/* 这里可以是一个 Outlet 或者具体 Layout */}
                  <AddParentChildTasks />
                </RoleGuard>
              )
            },
            {
              path: 'edit/:course_id/:id',
              element: (
                <RoleGuard ctrl="resource_parent_child" role={['get_list']} rolestring="get_list">
                  {/* 这里可以是一个 Outlet 或者具体 Layout */}
                  <EditParentChildTasks />
                </RoleGuard>
              )
            },
            {
              path: 'view/:course_id/:res_id',
              element: (
                <RoleGuard
                  ctrl="resource_parent_child"
                  role={['get_report']}
                  rolestring="get_report"
                >
                  {/* 这里可以是一个 Outlet 或者具体 Layout */}
                  <ViewStudents />
                </RoleGuard>
              )
            }
          ]
        },
        {
          path: 'schoolResource',
          element: (
            <RoleGuard ctrl="resource_folder" role={['get_list']} rolestring="get_list">
              {/* 这里可以是一个 Outlet 或者具体 Layout */}
              <Outlet />
            </RoleGuard>
          ),
          children: [
            { element: <GeneralSchoolResource /> },
            { path: ':course_id', element: <GeneralSchoolResource /> },
            { path: 'trashCan', element: <TrashCanList /> }
          ]
        },
        {
          path: 'extra-resource',
          element: (
            <RoleGuard ctrl="extra_folder" role={['get_list']} rolestring="get_list">
              <Outlet />
            </RoleGuard>
          ),
          children: [
            { element: <ExtraResource /> },
            { path: ':course_id', element: <ExtraResource /> }
          ]
        },
        {
          path: 'shool-resource-new',
          children: [
            { element: <GeneralSchoolResourceList /> },
            { path: ':course_id', element: <GeneralSchoolResourceList /> }
          ]
        },
        { path: 'lessonPlanTools', element: <LessonPlanTools /> },
        {
          path: 'platform-resource-library',
          element: (
            <RoleGuard ctrl="resource_resource_hub" role={['get_list']} rolestring="get_list">
              <PlatformResourceLibrary />
            </RoleGuard>
          )
        },
        {
          path: 'aplus-question-bank',
          children: [
            {
              element: <Navigate to={PATH_DASHBOARD.general.aplusQuestionBank.questions} replace />
            },
            {
              path: 'questions',
              element: (
                <AplusGuard>
                  <AplusQuestionBank />
                </AplusGuard>
              )
            },
            {
              path: 'questions/new',
              element: (
                <AplusGuard>
                  <AplusQuestionBank />
                </AplusGuard>
              )
            },
            {
              path: 'questions/:adapterId',
              element: (
                <AplusGuard>
                  <AplusQuestionBank />
                </AplusGuard>
              )
            },
            {
              path: 'bulk-import',
              element: (
                <AplusGuard>
                  <AplusBulkImport />
                </AplusGuard>
              )
            }
          ]
        },
        {
          path: 'aplus-campaign',
          children: [
            {
              element: (
                <AplusGuard>
                  <AplusCampaignList />
                </AplusGuard>
              )
            },
            {
              path: 'new',
              element: (
                <AplusGuard>
                  <AplusCampaignList />
                </AplusGuard>
              )
            },
            {
              path: ':campaignId',
              element: (
                <AplusGuard>
                  <AplusCampaignDetail />
                </AplusGuard>
              )
            }
          ]
        },
        {
          path: 'internal-question-bank',
          children: [
            { element: <InternalQuestionBankFrame /> },
            { path: 'questions', element: <QuestionBankQuestions /> },
            { path: 'questions/new', element: <QuestionBankQuestions /> },
            { path: 'questions/:adapterId', element: <QuestionBankQuestions /> },
            { path: 'chinese-word-bank', element: <InternalQuestionBankFrame /> },
            { path: 'taxonomy', element: <InternalQuestionBankFrame /> }
          ]
        },
        { path: 'resource/:ref_id', element: <ResourceCheck /> },
        {
          path: 'account',

          children: [
            {
              path: 'staff/list',
              element: (
                <RoleGuard ctrl="resource_staff" role={['get_list']} rolestring="get_list">
                  <StaffList />
                </RoleGuard>
              )
            },
            {
              path: 'authority/list',
              element: (
                <RoleGuard ctrl="resource_role_group" role={['get_list']} rolestring="get_list">
                  <AuthorityList />
                </RoleGuard>
              )
            },
            {
              path: 'authority/add',
              element: (
                <RoleGuard ctrl="resource_role_group" role={['add']} rolestring="add">
                  <AuthorityAddOrEdit />
                </RoleGuard>
              )
            },
            {
              path: 'authority/edit/:id',
              element: (
                <RoleGuard ctrl="resource_role_group" role={['get_list']} rolestring="get_list">
                  <AuthorityAddOrEdit />
                </RoleGuard>
              )
            }
          ]
        }
      ]
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const AutoLogin = Loadable(lazy(() => import('../pages/login-loading')));
const Merchant = Loadable(lazy(() => import('../pages/authentication/Merchant')));
const Register = Loadable(lazy(() => import('../pages/authentication/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')));
const GodotPreviewTest = Loadable(lazy(() => import('../pages/godot-preview-test')));
// Dashboard
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const DashboardHome = Loadable(lazy(() => import('../pages/dashboard/DashboardHome')));
const InternalQuestionBankFrame = Loadable(
  lazy(() => import('../pages/dashboard/InternalQuestionBankFrame'))
);
const AplusQuestionBank = Loadable(lazy(() => import('../pages/dashboard/aplusQuestionBank')));
const AplusBulkImport = Loadable(
  lazy(() => import('../pages/dashboard/aplusQuestionBank/bulkImport'))
);
const AplusCampaignList = Loadable(lazy(() => import('../pages/dashboard/aplusCampaign')));
const AplusCampaignDetail = Loadable(
  lazy(() => import('../pages/dashboard/aplusCampaign/CampaignDetail'))
);
const GeneralSchoolBasedActivities = Loadable(
  lazy(() => import('../pages/dashboard/GeneralSchoolBasedActivities'))
);
const GeneralSchoolBasedActivityForm = Loadable(
  lazy(() => import('../pages/dashboard/GeneralSchoolBasedActivityForm'))
);
const GeneralSchoolBasedActivityEditForm = Loadable(
  lazy(() => import('../pages/dashboard/GeneralSchoolBasedActivityEditForm'))
);
const GeneralSchoolBasedActivityViewStudentReport = Loadable(
  lazy(() => import('../pages/dashboard/GeneralSchoolBasedActivityViewStudentReport'))
);
const GeneralSchoolBasedActivityViewStudentReportDetail = Loadable(
  lazy(() => import('../pages/dashboard/GeneralSchoolBasedActivityViewStudentReportDetail'))
);
const CurriculumActivities = Loadable(
  lazy(() => import('../pages/dashboard/CurriculumActivities'))
);
const CurriculumActivitiesNew = Loadable(
  lazy(() => import('../pages/dashboard/curriculumActivities/CurriculumActivitiesNew'))
);
const CurriculumActivitiesEdit = Loadable(
  lazy(() => import('../pages/dashboard/curriculumActivities/CurriculumActivitiesEdit'))
);
const GeneralSchoolBasedActivityViewState = Loadable(
  lazy(() => import('../pages/dashboard/GeneralSchoolBasedActivityViewState'))
);
const GeneralSchoolBasedActivityViewStateStudent = Loadable(
  lazy(() => import('../pages/dashboard/GeneralSchoolBasedActivityViewStateStudent'))
);
const GeneralSchoolBasedActivityViewStateStudentDetail = Loadable(
  lazy(() => import('../pages/dashboard/GeneralSchoolBasedActivityViewStateStudentDetail'))
);

const GeneralSchoolCourse = Loadable(lazy(() => import('../pages/dashboard/GeneralSchoolCourse')));
const GeneralSmallActivity = Loadable(
  lazy(() => import('../pages/dashboard/GeneralSmallActivity'))
);
const GeneralExhibition = Loadable(lazy(() => import('../pages/dashboard/GeneralExhibition')));
const GeneralExhibitionDetail = Loadable(
  lazy(() => import('../pages/dashboard/GeneralExhibitionDetail'))
);
const GeneralPlatform = Loadable(lazy(() => import('../pages/dashboard/GeneralPlatformResource')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const GeneralSchoolResource = Loadable(
  lazy(() => import('../pages/dashboard/GeneralSchoolResource'))
);
const ExtraResource = Loadable(lazy(() => import('../pages/dashboard/ExtraResource')));

const GeneralCustomList = Loadable(lazy(() => import('../pages/dashboard/GeneralCustomList')));
const GeneralCustomListDetail = Loadable(
  lazy(() => import('../pages/dashboard/GeneralCustomListDetail'))
);
const GeneralSchoolResourceList = Loadable(
  lazy(() => import('../pages/dashboard/GeneralSchoolResourceList'))
);
const TrashCanList = Loadable(
  Loadable(lazy(() => import('../components/_dashboard/SchoolRepository/trashCan/TrashCanList')))
);
const ResourceCheck = Loadable(lazy(() => import('../components/resource/Checking')));
// const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));
// const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
// const EcommerceProductDetails = Loadable(
//   lazy(() => import('../pages/dashboard/EcommerceProductDetails'))
// );
// const EcommerceProductList = Loadable(
//   lazy(() => import('../pages/dashboard/EcommerceProductList'))
// );
// const EcommerceProductCreate = Loadable(
//   lazy(() => import('../pages/dashboard/EcommerceProductCreate'))
// );
// const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));
// const EcommerceInvoice = Loadable(lazy(() => import('../pages/dashboard/EcommerceInvoice')));
// const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
// const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
// const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));
// const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
// const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
// const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
// const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
// const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));
// const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
// const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
// const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
// const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));
// const Setting = Loadable(lazy(() => import('../pages/dashboard/Setting')));
const ParentChildTaskList = Loadable(
  lazy(() => import('../pages/dashboard/parentChildTask/ParentChildTaskList'))
);
const AddParentChildTasks = Loadable(
  lazy(() => import('../pages/dashboard/parentChildTask/AddParentChildTasks'))
);
const EditParentChildTasks = Loadable(
  lazy(() => import('../pages/dashboard/parentChildTask/EditParentChildTasks'))
);
const ViewStudents = Loadable(
  lazy(() => import('../pages/dashboard/parentChildTask/ViewStudents'))
);
const LessonPlanTools = Loadable(lazy(() => import('../pages/dashboard/LessonPlanTools')));
const QuestionBankQuestions = Loadable(
  lazy(() => import('../pages/dashboard/questionBankQuestions'))
);
const PlatformResourceLibrary = Loadable(
  lazy(() => import('../components/_dashboard/NewPlatformResource'))
);
const StaffList = Loadable(lazy(() => import('../pages/dashboard/StaffList')));
const AuthorityList = Loadable(lazy(() => import('../pages/dashboard/authorityList')));
const AuthorityAddOrEdit = Loadable(lazy(() => import('../pages/authority/AuthorityAddOrEdit')));
// Main
// const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
// const About = Loadable(lazy(() => import('../pages/About')));
// const Contact = Loadable(lazy(() => import('../pages/Contact')));
// const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
// const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
// const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
// Components
// const ComponentsOverview = Loadable(lazy(() => import('../pages/ComponentsOverview')));
// const Color = Loadable(
//   lazy(() => import('../pages/components-overview/foundations/FoundationColors'))
// );
// const Typography = Loadable(
//   lazy(() => import('../pages/components-overview/foundations/FoundationTypography'))
// );
// const Shadows = Loadable(
//   lazy(() => import('../pages/components-overview/foundations/FoundationShadows'))
// );
// const Grid = Loadable(
//   lazy(() => import('../pages/components-overview/foundations/FoundationGrid'))
// );
// const Icons = Loadable(
//   lazy(() => import('../pages/components-overview/foundations/FoundationIcons'))
// );
// const Accordion = Loadable(
//   lazy(() => import('../pages/components-overview/material-ui/Accordion'))
// );
// const Alert = Loadable(lazy(() => import('../pages/components-overview/material-ui/Alert')));
// const Autocomplete = Loadable(
//   lazy(() => import('../pages/components-overview/material-ui/Autocomplete'))
// );
// const Avatar = Loadable(lazy(() => import('../pages/components-overview/material-ui/Avatar')));
// const Badge = Loadable(lazy(() => import('../pages/components-overview/material-ui/Badge')));
// const Breadcrumb = Loadable(
//   lazy(() => import('../pages/components-overview/material-ui/Breadcrumb'))
// );
// const Buttons = Loadable(lazy(() => import('../pages/components-overview/material-ui/buttons')));
// const Checkbox = Loadable(
//   lazy(() => import('../pages/components-overview/material-ui/Checkboxes'))
// );
// const Chip = Loadable(lazy(() => import('../pages/components-overview/material-ui/chips')));
// const Dialog = Loadable(lazy(() => import('../pages/components-overview/material-ui/dialog')));
// const Label = Loadable(lazy(() => import('../pages/components-overview/material-ui/Label')));
// const List = Loadable(lazy(() => import('../pages/components-overview/material-ui/Lists')));
// const Menu = Loadable(lazy(() => import('../pages/components-overview/material-ui/Menus')));
// const Pagination = Loadable(
//   lazy(() => import('../pages/components-overview/material-ui/Pagination'))
// );
// const Pickers = Loadable(lazy(() => import('../pages/components-overview/material-ui/pickers')));
// const Popover = Loadable(lazy(() => import('../pages/components-overview/material-ui/Popover')));
// const Progress = Loadable(lazy(() => import('../pages/components-overview/material-ui/progress')));
// const RadioButtons = Loadable(
//   lazy(() => import('../pages/components-overview/material-ui/RadioButtons'))
// );
// const Rating = Loadable(lazy(() => import('../pages/components-overview/material-ui/Rating')));
// const Slider = Loadable(lazy(() => import('../pages/components-overview/material-ui/Slider')));
// const Snackbar = Loadable(lazy(() => import('../pages/components-overview/material-ui/Snackbar')));
// const Stepper = Loadable(lazy(() => import('../pages/components-overview/material-ui/stepper')));
// const Switches = Loadable(lazy(() => import('../pages/components-overview/material-ui/Switches')));
// const Table = Loadable(lazy(() => import('../pages/components-overview/material-ui/table')));
// const Tabs = Loadable(lazy(() => import('../pages/components-overview/material-ui/Tabs')));
// const Textfield = Loadable(
//   lazy(() => import('../pages/components-overview/material-ui/textfield'))
// );
// const Timeline = Loadable(lazy(() => import('../pages/components-overview/material-ui/Timeline')));
// const Tooltip = Loadable(lazy(() => import('../pages/components-overview/material-ui/Tooltip')));
// const TransferList = Loadable(
//   lazy(() => import('../pages/components-overview/material-ui/transfer-list'))
// );
// const TreeView = Loadable(lazy(() => import('../pages/components-overview/material-ui/TreeView')));
// const DataGrid = Loadable(lazy(() => import('../pages/components-overview/material-ui/data-grid')));
// //
// const Charts = Loadable(lazy(() => import('../pages/components-overview/extra/Charts')));
// const Map = Loadable(lazy(() => import('../pages/components-overview/extra/Map')));
// const Editor = Loadable(lazy(() => import('../pages/components-overview/extra/Editor')));
// const CopyToClipboard = Loadable(
//   lazy(() => import('../pages/components-overview/extra/CopyToClipboard'))
// );
// const Upload = Loadable(lazy(() => import('../pages/components-overview/extra/Upload')));
// const Carousel = Loadable(lazy(() => import('../pages/components-overview/extra/Carousel')));
// const MultiLanguage = Loadable(
//   lazy(() => import('../pages/components-overview/extra/MultiLanguage'))
// );
// const Animate = Loadable(lazy(() => import('../pages/components-overview/extra/animate')));
// const MegaMenu = Loadable(lazy(() => import('../pages/components-overview/extra/MegaMenu')));
// const FormValidation = Loadable(
//   lazy(() => import('../pages/components-overview/extra/form-validation'))
// );
